import { subDays } from "date-fns"
import type { AudienceFilter } from "@/entities/audience"

export interface AudiencePreset {
	id: string
	label: string
	hint: string
	/** Фильтр в момент выбора: даты считаются от сегодняшнего дня. */
	filter: () => AudienceFilter
	/** Этот ли набор условий сейчас в рассылке. */
	matches: (filter: AudienceFilter) => boolean
}

const keysOf = (filter: AudienceFilter) =>
	Object.entries(filter)
		.filter(([, value]) => value !== undefined && value !== "")
		.map(([key]) => key)

/** Ровно эти условия с этими значениями, без лишних. */
const exactly =
	(expected: AudienceFilter) =>
	(filter: AudienceFilter): boolean => {
		const keys = keysOf(filter)
		const wanted = keysOf(expected)
		return (
			keys.length === wanted.length &&
			wanted.every((key) => filter[key as keyof AudienceFilter] === expected[key as keyof AudienceFilter])
		)
	}

const NEW_DAYS = 7

/**
 * Готовые группы: то, что менеджер чаще всего собирал бы условиями руками. Каждая это
 * обычный фильтр аудитории, поэтому число людей считает тот же запрос, что и для своих
 * условий, а в черновике сохраняется сам фильтр.
 */
export const AUDIENCE_PRESETS: AudiencePreset[] = [
	{
		id: "all",
		label: "Все в боте",
		hint: "Кроме заблокировавших",
		filter: () => ({}),
		matches: exactly({}),
	},
	{
		id: "in-channel",
		label: "В канале",
		hint: "Подписаны на канал клуба",
		filter: () => ({ channel: "member" }),
		matches: exactly({ channel: "member" }),
	},
	{
		id: "not-in-channel",
		label: "Не в канале",
		hint: "Все в боте, кроме подписчиков",
		filter: () => ({ channel: "not_member" }),
		matches: exactly({ channel: "not_member" }),
	},
	{
		id: "no-account",
		label: "Нет аккаунта в приложении",
		hint: "Пишут только боту",
		filter: () => ({ channel: "no_account" }),
		matches: exactly({ channel: "no_account" }),
	},
	{
		id: "new",
		label: `Новые за ${NEW_DAYS} дней`,
		hint: "Впервые написали боту",
		filter: () => ({ joinedAfter: subDays(new Date(), NEW_DAYS).toISOString() }),
		// Дата в черновике застывает в момент выбора: узнаём группу по ширине окна.
		matches: (filter) => {
			if (keysOf(filter).join() !== "joinedAfter" || !filter.joinedAfter) return false
			const days = (Date.now() - new Date(filter.joinedAfter).getTime()) / 86_400_000
			return Math.abs(days - NEW_DAYS) < 1
		},
	},
	{
		id: "stuck",
		label: "Не дошли до конца воронки",
		hint: "Бот ждёт от них ответа",
		filter: () => ({ state: "waiting" }),
		matches: exactly({ state: "waiting" }),
	},
	{
		id: "inactive",
		label: "Не писали боту 30 дней",
		hint: "Давно молчат",
		filter: () => ({ inactiveDays: 30 }),
		matches: exactly({ inactiveDays: 30 }),
	},
	{
		id: "not-reading",
		label: "Не открывали материалы 14 дней",
		hint: "Аккаунт есть, но не читают",
		filter: () => ({ notReadDays: 14 }),
		matches: exactly({ notReadDays: 14 }),
	},
	{
		id: "beginner",
		label: "Не начали путь новичка",
		hint: "Ни одного шага",
		filter: () => ({ beginner: "not_started" }),
		matches: exactly({ beginner: "not_started" }),
	},
	{
		id: "paid",
		label: "Оплатили в боте",
		hint: "Хотя бы одна оплата",
		filter: () => ({ payments: "has" }),
		matches: exactly({ payments: "has" }),
	},
	{
		id: "not-paid",
		label: "Ещё не оплачивали",
		hint: "Ни одной оплаты",
		filter: () => ({ payments: "none" }),
		matches: exactly({ payments: "none" }),
	},
]

export const presetOf = (filter: AudienceFilter) =>
	AUDIENCE_PRESETS.find((preset) => preset.matches(filter))

/** Ровно одна группа сценария и ничего больше. */
export const groupOf = (filter: AudienceFilter) =>
	keysOf(filter).join() === "tagId" ? filter.tagId : undefined
